import {
  NonSegmentEvents,
  UserBrowser,
  trackD2CNonSegmentAnalytics,
  useUserBrowser,
} from '@/analytics';
import {
  EventPropertiesInput,
  buildEventProperties,
} from '@/analytics/buildAnalyticsPayloads';
import { fetchProductConfig } from '@/server-actions/fetch-product-config';
import { setBugsnagUser } from '@/utils/bugsnagUtils';
import { getPaths } from '@/utils/urlPaths';
import { useAnalytics } from '@sales-domain/analytics-react';
import { useParams, usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

export function useIdentifyBrowserSession() {
  // right now GameStop and Affiliate use different identifiers than
  // the other flows so grab both version of partner and product
  // and coalesce them to the "official" once
  let analyticProduct: EventPropertiesInput['products'] | undefined;

  const { partner, partnerIdentifier, product, productIdentfier } = useParams();
  const pathname = usePathname();
  const paths = pathname.split('/');
  const presentation =
    useParams().presentation ?? getPaths(pathname).presentation;
  const isSteps = getPaths(pathname).presentation === 'steps';

  const partnerToUse = partner ?? partnerIdentifier;
  const productToUse = product ?? productIdentfier;

  const analytics = useAnalytics();
  const userBrowser = useUserBrowser({
    partnerIdentifier: partnerToUse as string,
  });
  const initialLoadRef = useRef(false);
  const pageName = pathname.split('/').pop();

  useEffect(() => {
    async function trackAnalytics() {
      if (typeof userBrowser !== 'undefined' && !initialLoadRef.current) {
        async function getProductData() {
          const productConfig = await fetchProductConfig();
          const browserSession = UserBrowser.get();

          analyticProduct = [
            {
              id: '',
              productId: productConfig?.success
                ? productConfig.data.config.productSku
                : '',
              sku: productConfig?.success
                ? productConfig.data.config.productSku
                : '',
              category: productConfig.success
                ? productConfig.data.config.productType
                : undefined,
              name: productConfig.success
                ? productConfig.data.config.retailName
                : undefined,
              brand: productConfig.success
                ? productConfig.data.config.internalPartner
                : undefined,
              price: productConfig.success
                ? productConfig.data.config.price
                : undefined,
              quantity: 1,
              coupon: browserSession?.sessionQueryParameters?.coupon,
            },
          ];
        }

        if (isSteps) {
          await getProductData();
        }
        analytics.identify(userBrowser.UserBrowserId, {});
        trackD2CNonSegmentAnalytics(
          NonSegmentEvents.InitialPageLoad,
          buildEventProperties({
            event: NonSegmentEvents.InitialPageLoad,
            customer: undefined,
            customerSession: undefined,
            products: analyticProduct,
            productOffer: undefined,
            extraData: {
              productUrlSlug: productToUse ?? '',
              partnerUrlSlug: partnerToUse ?? '',
              presentationUrlSlug: presentation ?? '',
              pageName,
            },
          }),
        );
        initialLoadRef.current = true;
      }
      setBugsnagUser(userBrowser?.UserBrowserId as string);
    }

    trackAnalytics();
  }, [
    analytics,
    pageName,
    partnerToUse,
    presentation,
    productToUse,
    userBrowser,
  ]);
}
